/**
 * PassportEmbed.tsx
 * Minimal embed page for accessibility passport widget (no app layout)
 * Renders the same widget as "Anteprima dal vivo" but standalone
 */

import React, { useEffect, useState, useRef } from 'react';
import { CheckCircle, ExternalLink, MapPin, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { loadStructurePair, getAllPublicStructures } from '../firebase/firestore';
import logger from '../utils/logger';

interface PassportEmbedProps {
  structureId?: string;
  portal?: string;
  theme?: 'blue' | 'brand' | 'orange';
  language?: 'it' | 'en' | 'de' | 'fr';
  size?: 'compact' | 'standard' | 'detailed';
}

const getWidgetStyles = (theme: string) => {
  switch (theme) {
    case 'orange':
      return {
        gradient: 'bg-gradient-to-br from-orange-500 to-orange-600',
        primary: 'text-orange-600',
        primaryBg: 'bg-orange-500',
        accent: 'bg-orange-100 text-orange-600',
        border: 'border-orange-600'
      };
    case 'brand':
      return {
        gradient: 'bg-gradient-travelin',
        primary: 'text-travelin-blue',
        primaryBg: 'bg-gradient-travelin',
        accent: 'bg-blue-100 text-travelin-blue',
        border: 'border-travelin-blue'
      };
    default: // blue
      return {
        gradient: 'bg-gradient-travelin',
        primary: 'text-travelin-blue',
        primaryBg: 'bg-gradient-travelin',
        accent: 'bg-blue-100 text-travelin-blue',
        border: 'border-travelin-blue'
      };
  }
};

const getWidgetText = (lang: string) => {
  const texts: any = {
    it: {
      title: 'Passaporto Accessibilità',
      digitalTitle: 'Passaporto Digitale di Accessibilità',
      complete: "Informazioni complete sull'accessibilità",
      description: "Documentazione completa dell'accessibilità disponibile. Questo passaporto fornisce informazioni dettagliate su caratteristiche, servizi e strutture di accessibilità per tutti i visitatori.",
      viewButton: 'Visualizza Passaporto',
      viewComplete: 'Visualizza Passaporto Completo',
      verified: 'Verificato',
      poweredBy: 'Offerto da Travelin',
      certified: 'Certificato da Travelin • Ultimo aggiornamento 2 giorni fa',
      contact: 'Contatta'
    },
    en: {
      title: 'Accessibility Passport',
      digitalTitle: 'Digital Accessibility Passport',
      complete: 'Complete accessibility information',
      description: 'Comprehensive accessibility documentation available. This passport provides detailed information about accessibility features, services, and facilities for all visitors.',
      viewButton: 'View Passport',
      viewComplete: 'View Complete Passport',
      verified: 'Verified',
      poweredBy: 'Powered by Travelin',
      certified: 'Certified by Travelin • Last updated 2 days ago',
      contact: 'Contact'
    },
    de: {
      title: 'Barrierefreiheits-Pass',
      digitalTitle: 'Digitaler Barrierefreiheits-Pass',
      complete: 'Vollständige Barrierefreiheitsinformationen',
      description: 'Umfassende Barrierefreiheitsdokumentation verfügbar. Dieser Pass bietet detaillierte Informationen über Barrierefreiheitsmerkmale, Services und Einrichtungen für alle Besucher.',
      viewButton: 'Pass Anzeigen',
      viewComplete: 'Vollständigen Pass Anzeigen',
      verified: 'Verifiziert',
      poweredBy: 'Powered by Travelin',
      certified: 'Zertifiziert von Travelin • Zuletzt aktualisiert vor 2 Tagen',
      contact: 'Kontakt'
    },
    fr: {
      title: "Passeport d'Accessibilité",
      digitalTitle: "Passeport Numérique d'Accessibilité",
      complete: "Informations complètes sur l'accessibilité",
      description: "Documentation complète sur l'accessibilité disponible. Ce passeport fournit des informations détaillées sur les caractéristiques, services et installations d'accessibilité pour tous les visiteurs.",
      viewButton: 'Voir le Passeport',
      viewComplete: 'Voir le Passeport Complet',
      verified: 'Vérifié',
      poweredBy: 'Propulsé par Travelin',
      certified: 'Certifié par Travelin • Dernière mise à jour il y a 2 jours',
      contact: 'Contact'
    }
  };
  return texts[lang] || texts.it;
};

const getWidgetDimensions = (size: string) => {
  switch (size) {
    case 'compact':
      return {
        containerClass: 'max-w-xs p-3',
        iconSize: 'w-8 h-8',
        titleSize: 'text-m',
        padding: 'p-3'
      };
    case 'detailed':
      return {
        containerClass: 'max-w-xl p-8',
        iconSize: 'w-16 h-16',
        titleSize: 'text-xxl',
        padding: 'p-8'
      };
    default: // standard
      return {
        containerClass: 'max-w-md p-5',
        iconSize: 'w-12 h-12',
        titleSize: 'text-xl',
        padding: 'p-5'
      };
  }
};

export function PassportEmbed({ structureId, portal, theme = 'blue', language = 'it', size = 'standard' }: PassportEmbedProps) {
  const [loading, setLoading] = useState(true);
  const [structure, setStructure] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [publicId, setPublicId] = useState<string | null>(null); // Store public ID (slug or id_backend)
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;

    async function loadData() {
      if (!structureId) {
        setError('Parametro structureId mancante');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        logger.info('[PassportEmbed] Loading structure:', structureId);
        
        // Try to load structure data
        const publicDocs = await getAllPublicStructures();
        const norm = (s: string) => String(s || '').trim().toLowerCase();
        
        // Find by ID or slug
        const pubHit = publicDocs.find((d: any) => {
          const ids = [
            d.id,
            d.slug,
            d.id_backend,
            d.structure?.id,
          ].map(norm);
          return ids.includes(norm(structureId));
        });

        if (!active) return;

        if (pubHit) {
          const pair = await loadStructurePair(pubHit.id_backend || pubHit.id);
          if (active) {
            // Merge private and public data (prefer public modules/photos, private for everything else)
            const merged = {
              ...pair.privateDoc,
              ...pair.publicDoc,
              modules: pair.publicDoc?.modules || pair.privateDoc?.modules || [],
              photos: pair.publicDoc?.photos || pair.privateDoc?.photos || [],
              public_infos: pair.publicDoc?.public_infos || pair.privateDoc?.public_infos || {},
            };
            setStructure(merged);
            // Store public identifier (prefer slug, fallback to id_backend or id)
            const publicIdentifier = pubHit.slug || pubHit.id_backend || pubHit.id;
            setPublicId(publicIdentifier);
            setLoading(false);
          }
        } else {
          throw new Error('Struttura non trovata');
        }
      } catch (err: any) {
        logger.error('[PassportEmbed] Error loading structure:', err);
        if (active) {
          setError(err.message || 'Errore nel caricamento');
          setLoading(false);
        }
      }
    }

    loadData();
    return () => { active = false; };
  }, [structureId]);

  // Auto-height: send postMessage to parent when content size changes
  useEffect(() => {
    // Only run client-side
    if (typeof window === 'undefined' || !window.parent) return;

    const sendHeight = () => {
      try {
        // Measure full document height
        const height = Math.max(
          document.documentElement.scrollHeight,
          document.body.scrollHeight,
          containerRef.current?.scrollHeight || 0
        );

        // Send message to parent
        window.parent.postMessage(
          {
            type: 'travelin-passport-height',
            height,
            structureId: structureId || null, // Debug info
          },
          '*' // Allow any origin (parent will validate)
        );

        // Log only in development
        if (import.meta.env.DEV) {
          logger.info('[PassportEmbed] Height sent to parent:', height);
        }
      } catch (err) {
        // Silently fail (cross-origin restrictions, etc.)
        if (import.meta.env.DEV) {
          logger.warn('[PassportEmbed] Failed to send height:', err);
        }
      }
    };

    // Send height on mount and when structure loads
    if (!loading) {
      // Small delay to ensure DOM is fully rendered
      const timer = setTimeout(sendHeight, 100);

      // Set up ResizeObserver to detect content changes
      let resizeObserver: ResizeObserver | null = null;
      if (containerRef.current && typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          sendHeight();
        });
        resizeObserver.observe(containerRef.current);
      }

      // Fallback: window resize listener
      window.addEventListener('resize', sendHeight);

      return () => {
        clearTimeout(timer);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
        window.removeEventListener('resize', sendHeight);
      };
    }
  }, [loading, structure, structureId]);

  // Helper to get structure name
  const getStructureName = (struct: any) => {
    const extractValue = (val: any) => {
      if (val === null || val === undefined) return '';
      if (typeof val === 'object' && val.value !== undefined) {
        return String(val.value);
      }
      return String(val);
    };

    if (Array.isArray(struct?.modules) && struct.modules.length > 0) {
      const firstModule = struct.modules[0];
      if (firstModule?.content) {
        let nomeStruttura = extractValue(firstModule.content["6.1"]);
        if (!nomeStruttura) {
          nomeStruttura = extractValue(firstModule.content["NOME STRUTTURA"]);
        }
        if (nomeStruttura && nomeStruttura.trim()) {
          return nomeStruttura;
        }
      }
    }
    
    return struct?.structure?.accommodation_data?.accommodation_name || struct?.name || '';
  };

  const handleViewPassport = () => {
    // Open full passport on travelin.me travelers hub
    // Use public ID (slug preferred) instead of private structure ID
    const identifier = publicId || structureId;
    const passportUrl = `https://travelin.me/?view=travelers-hub&structure=${identifier}`;
    window.open(passportUrl, '_blank');
  };

  const styles = getWidgetStyles(theme);
  const text = getWidgetText(language);
  const dimensions = getWidgetDimensions(size);

  if (loading) {
    return (
      <div ref={containerRef} className="min-h-screen flex items-center justify-center bg-transparent">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-travelin-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento widget...</p>
        </div>
      </div>
    );
  }

  if (error || !structure) {
    return (
      <div ref={containerRef} className="min-h-screen flex items-center justify-center bg-transparent p-4">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Widget non disponibile</h2>
          <p className="text-gray-600">{error || 'Struttura non trovata'}</p>
        </div>
      </div>
    );
  }

  // Render widget based on size
  return (
    <div ref={containerRef} className="min-h-screen flex items-center justify-center bg-transparent p-0">
      <div className={`bg-white border border-gray-200 rounded-lg ${dimensions.padding} shadow-sm ${dimensions.containerClass}`}>
        {size === 'compact' && (
          <>
            <div className="flex items-center gap-3">
              <div className={`${dimensions.iconSize} rounded-lg ${styles.gradient} flex items-center justify-center`}>
                <CheckCircle className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="mb-1">
                  <span className={`text-s font-bold ${styles.primary}`}>{text.title}</span>
                </div>
                <p className="text-ss text-gray-600 truncate">{getStructureName(structure)}</p>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center gap-1">
                <img src="/travelin-logo.svg" alt="Travelin" className="h-3 w-auto" />
                <span className={`text-ss ${styles.primary} font-medium`}>{text.verified}</span>
              </div>
              <Button size="sm" variant="outline" className="h-6 text-ss px-2" onClick={handleViewPassport}>
                {text.viewButton}
              </Button>
            </div>
          </>
        )}

        {size === 'standard' && (
          <>
            <div className="flex items-start gap-4 mb-4">
              <div className={`${dimensions.iconSize} rounded-xl ${styles.gradient} flex items-center justify-center shadow-sm`}>
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="mb-2">
                  <span className={`${dimensions.titleSize} font-bold ${styles.primary}`}>{text.title}</span>
                </div>
                <h4 className="font-semibold text-gray-900 leading-tight">{getStructureName(structure)}</h4>
                <p className="text-s text-gray-600 mt-1">{text.complete}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {(structure?.public_infos?.titlets || []).slice(0, 2).map((strength: any, index: number) => (
                <span key={index} className={`inline-flex items-center px-2 py-1 rounded-full text-ss font-medium ${styles.accent}`}>
                  ✓ {strength}
                </span>
              ))}
              {(structure?.public_infos?.titlets || []).length > 2 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-ss font-medium bg-gray-100 text-gray-700">
                  +{(structure?.public_infos?.titlets || []).length - 2} more
                </span>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-ss text-gray-500">
                <img src="/travelin-logo.svg" alt="Travelin" className="h-4 w-auto" />
                <span>{text.poweredBy}</span>
              </div>
              <Button size="sm" variant="outline" className={`${styles.border} ${styles.primary}`} onClick={handleViewPassport}>
                <ExternalLink className="w-3 h-3 mr-1" />
                {text.viewButton}
              </Button>
            </div>
          </>
        )}

        {size === 'detailed' && (
          <>
            <div className="flex items-start gap-4 mb-5">
              <div className={`${dimensions.iconSize} rounded-xl ${styles.gradient} flex items-center justify-center shadow-lg`}>
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <div className="mb-2">
                  <span className={`${dimensions.titleSize} font-bold ${styles.primary}`}>{text.digitalTitle}</span>
                </div>
                <h3 className="text-l font-semibold text-gray-900 leading-tight mb-1">{getStructureName(structure)}</h3>
                <p className="text-s text-gray-600">{structure?.structure?.accommodation_data?.type || ''}</p>
                <div className="flex items-center gap-2 mt-2">
                  <MapPin className="w-3 h-3 text-gray-400" />
                  <span className="text-ss text-gray-500">{structure?.structure?.accommodation_data?.address?.city || ''}</span>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-s text-gray-700 leading-relaxed mb-3">
                {text.description}
              </p>

              <div className="flex flex-wrap gap-2">
                {(structure?.public_infos?.titlets || []).map((strength: any, index: number) => (
                  <span key={index} className={`inline-flex items-center px-3 py-1 rounded-full text-ss font-medium ${styles.accent} border`}>
                    ✓ {strength}
                  </span>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-ss text-gray-500">
                  <img src="/travelin-logo.svg" alt="Travelin" className="h-4 w-auto" />
                  <span>{text.certified}</span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button size="sm" className={`flex-1 ${styles.gradient} text-white`} onClick={handleViewPassport}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {text.viewComplete}
                </Button>
                <Button size="sm" variant="outline" className="border-gray-300">
                  <Phone className="w-4 h-4 mr-2" />
                  {text.contact}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
